import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  HostListener,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  HttpClient,
  HttpClientModule,
  HttpHeaders,
} from '@angular/common/http';
import { AgGridModule } from 'ag-grid-angular';
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  CellClassParams,
  GridOptions,
} from 'ag-grid-community';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';

type Option = { id: number | string; label: string };

@Component({
  selector: 'app-order-college',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, HttpClientModule, AgGridModule],
  templateUrl: './order-college.component.html',
  styleUrls: ['./order-college.component.scss'],
})
export class OrderCollegeComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('gridWrapper', { static: false })
  gridWrapper!: ElementRef<HTMLDivElement>;

  form: FormGroup;

  states: Option[] = [];
  districts: Option[] = [];
  courses: Option[] = [];

  columnDefs: ColDef[] = [];
  rowData: any[] = [];
  private gridApi!: GridApi;

  // gridOptions with getRowId so applyTransaction matches rows by college_id
  gridOptions: GridOptions = {
    getRowId: (params) => params.data?.college_id,
  };

  loading = false;
  error: string | null = null;
  success: string | null = null;

  // Track subscriptions
  private subs: Subscription[] = [];

  // Track edits: key = college_id, value = new order
  editedMap: Map<number | string, number> = new Map();

  // Track touched rows (clicked) — included in payload even if value equals original
  touchedSet: Set<number | string> = new Set();

  // Keep original values so we can revert on cancel
  originalOrderMap: Map<number | string, number> = new Map();

  saving = false;

  // When user clicks any college_order cell, editingMode becomes true
  editingMode = false;

  // store last used search params so we reuse them after save
  lastSearchParams: { state_id: any; district_id: any; course_id: any } | null =
    null;

  // ResizeObserver to monitor header/form heights
  private resizeObserver?: ResizeObserver;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private cd: ChangeDetectorRef,
    private router: Router
  ) {
    this.form = this.fb.group({
      state_id: [null, Validators.required],
      district_id: [null, Validators.required],
      course_id: [null, Validators.required],
    });

    // Grid columns - NO Actions column
    this.columnDefs = [
      {
        field: 'college_id',
        headerName: 'ID',
        minWidth: 80,
        sortable: true,
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'college_order',
        headerName: 'Order',
        minWidth: 100,
        filter: 'agNumberColumnFilter',
        valueGetter: (params) => params.data?.college_order ?? 0,
        cellStyle: { textAlign: 'center' },
        editable: true, // only editable column
        cellEditor: 'agNumberCellEditor', // numeric-only editor
        // valueSetter performs validation and tracks edits
        valueSetter: (params) => this.collegeOrderValueSetter(params),
        // CSS class to highlight edited cells
        cellClass: (params: CellClassParams) => {
          const id = params.data?.college_id;
          return this.editedMap.has(id) ? 'edited-order-cell' : '';
        },
      },
      {
        field: 'collage_name',
        headerName: 'College Name',
        minWidth: 300,
        flex: 2,
        sortable: true,
        filter: 'agTextColumnFilter',
        valueGetter: (params) =>
          params.data?.collage_name ?? params.data?.college_name ?? '',
      },
      {
        field: 'state',
        headerName: 'State',
        minWidth: 160,
        sortable: true,
        filter: 'agTextColumnFilter',
        valueGetter: (params) => (params.data?.state ?? '').trim(),
      },
      {
        field: 'district',
        headerName: 'District',
        minWidth: 160,
        sortable: true,
        filter: 'agTextColumnFilter',
        valueGetter: (params) => (params.data?.district ?? '').trim(),
      },
      {
        field: 'course_type',
        headerName: 'Course Type',
        minWidth: 180,
        sortable: true,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'smscap_eligible',
        headerName: 'SMScap Eligible',
        minWidth: 140,
        cellRenderer: (params) => {
          const v = params.value;
          const txt = v === 1 || v === '1' ? 'Yes' : 'No';
          const cls =
            v === 1 || v === '1' ? 'badge bg-success' : 'badge bg-secondary';
          const span = document.createElement('span');
          span.className = cls;
          span.innerText = txt;
          return span;
        },
        cellStyle: {
          display: 'flex',
          'align-items': 'center',
          'justify-content': 'center',
        },
      },

      {
        field: 'image',
        headerName: 'Image',
        minWidth: 120,
        maxWidth: 140,
        cellRenderer: (params) => this.imageRenderer(params),
        sortable: false,
        filter: false,
      },
    ];
  }

  ngOnInit(): void {
    this.loadStates();
    this.loadDistricts();
    this.loadCourses();
  }

  ngAfterViewInit(): void {
    // initial sizing
    setTimeout(() => this.setGridHeight(), 60);

    // Observe header and form size changes for dynamic resizing
    try {
      this.resizeObserver = new ResizeObserver(() => {
        this.setGridHeight();
      });
      const header = document.querySelector('.page-header') as HTMLElement;
      const form = document.querySelector('.filter-form') as HTMLElement;
      if (header) this.resizeObserver.observe(header);
      if (form) this.resizeObserver.observe(form);
    } catch (err) {
      // ResizeObserver might not be supported — window resize fallback below will handle it
      console.warn('ResizeObserver not available', err);
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.setGridHeight();
  }

  private headers() {
    return new HttpHeaders({
      Accept: '*/*',
      'Content-Type': 'application/json',
    });
  }

  // -------- Dropdown Loaders ----------
  private loadStates() {
    const s = this.http
      .get<any>('https://smscap.in/api/state', { headers: this.headers() })
      .subscribe({
        next: (res) => {
          const arr = this.normalizeArrayResponse(res);
          this.states = arr.map((o: any, i: number) => ({
            id: o?.id ?? i,
            label: o?.state?.trim() ?? o?.name ?? `State ${i}`,
          }));
        },
        error: () => (this.states = []),
      });
    this.subs.push(s);
  }

  private loadDistricts() {
    const s = this.http
      .get<any>('https://smscap.in/api/district', { headers: this.headers() })
      .subscribe({
        next: (res) => {
          const arr = this.normalizeArrayResponse(res);
          this.districts = arr.map((o: any, i: number) => ({
            id: o?.id ?? i,
            label: o?.district?.trim() ?? o?.name ?? `District ${i}`,
          }));
        },
        error: () => (this.districts = []),
      });
    this.subs.push(s);
  }

  private loadCourses() {
    const s = this.http
      .get<any>('https://smscap.in/api/coursetype', { headers: this.headers() })
      .subscribe({
        next: (res) => {
          const arr = this.normalizeArrayResponse(res);
          this.courses = arr.map((o: any, i: number) => ({
            id: o?.id ?? i,
            label: o?.type?.trim() ?? o?.course_name ?? `Course ${i}`,
          }));
        },
        error: () => (this.courses = []),
      });
    this.subs.push(s);
  }

  private normalizeArrayResponse(resp: any): any[] {
    if (!resp) return [];
    if (Array.isArray(resp)) return resp;
    if (resp.data && Array.isArray(resp.data)) return resp.data;
    const firstArray = Object.values(resp).find((v) => Array.isArray(v)) as
      | any[]
      | undefined;
    return firstArray ?? [];
  }

  // -------- Form Submit ----------
  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    // Save the exact params user searched with so we can reuse them after save
    const { state_id, district_id, course_id } = this.form.value;
    this.lastSearchParams = { state_id, district_id, course_id };

    this.loading = true;
    this.error = null;
    const url = `https://smscap.in/api/collegelists?state_id=${state_id}&district_id=${district_id}&course_id=${course_id}`;

    const s = this.http.get<any>(url, { headers: this.headers() }).subscribe({
      next: (data) => {
        if (data && Array.isArray(data.data)) {
          this.rowData = data.data;
        } else if (Array.isArray(data)) {
          this.rowData = data;
        } else {
          this.rowData = this.normalizeArrayResponse(data);
        }

        // save original orders for revert
        this.originalOrderMap.clear();
        this.rowData.forEach((r) => {
          this.originalOrderMap.set(r.college_id, Number(r.college_order ?? 0));
        });

        // clear touched/edited sets when new search results arrive
        this.editedMap.clear();
        this.touchedSet.clear();

        this.cd.detectChanges();
        setTimeout(() => this.sizeColumnsToFit(), 50);

        // recalc grid height because top area changed
        setTimeout(() => this.setGridHeight(), 80);
      },
      error: () => {
        this.rowData = [];
        this.error = 'Failed to fetch colleges. Please try again.';
      },
      complete: () => (this.loading = false),
    });
    this.subs.push(s);
  }

  // -------- Grid Helpers ----------
  onGridReady(event: GridReadyEvent) {
    this.gridApi = event.api;
    setTimeout(() => this.sizeColumnsToFit(), 100);
    setTimeout(() => this.setGridHeight(), 80);
  }

  sizeColumnsToFit() {
    try {
      if (this.gridApi) this.gridApi.sizeColumnsToFit();
    } catch {}
  }

  // When user clicks any cell — record touched id
  onCellClicked(event: any) {
    try {
      if (event?.colDef?.field === 'college_order') {
        this.editingMode = true;
        const id = event?.data?.college_id;
        if (id !== undefined && id !== null) {
          this.touchedSet.add(id);
        }
        this.cd.detectChanges();
      }
    } catch {}
  }

  // -------- Custom Renderers ----------
  private imageRenderer(params: any) {
    const url = params?.data?.image ?? '';
    const wrapper = document.createElement('div');
    wrapper.style.display = 'flex';
    wrapper.style.alignItems = 'center';
    wrapper.style.justifyContent = 'center';

    if (!url) {
      const none = document.createElement('div');
      none.innerText = '—';
      none.style.fontSize = '12px';
      none.style.color = '#666';
      wrapper.appendChild(none);
      return wrapper;
    }

    const img = document.createElement('img');
    img.src = url;
    img.alt = params?.data?.collage_name ?? 'image';
    img.style.width = '80px';
    img.style.height = '50px';
    img.style.objectFit = 'cover';
    img.style.borderRadius = '4px';
    wrapper.appendChild(img);
    return wrapper;
  }

  // valueSetter for college_order: validate integer and track edits
  private collegeOrderValueSetter(params: any): boolean {
    const newValueRaw = params.newValue;
    // treat empty as 0
    const parsed = String(newValueRaw).trim() === '' ? 0 : Number(newValueRaw);

    // allow only finite non-negative integers
    if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < 0) {
      // reject invalid value
      return false;
    }

    const current = Number(params.oldValue ?? params.data.college_order ?? 0);
    if (parsed === current) {
      // if value matches original, remove from editedMap if present
      const id = params.data?.college_id;
      const orig = this.originalOrderMap.get(id) ?? current;
      if (parsed === orig && this.editedMap.has(id)) {
        this.editedMap.delete(id);
      }
      return false; // no actual change
    }

    // apply to row data
    params.data.college_order = parsed;

    // store original if not already stored
    const id = params.data?.college_id;
    if (!this.originalOrderMap.has(id)) {
      this.originalOrderMap.set(id, Number(params.oldValue ?? 0));
    }

    // track edited
    this.editedMap.set(id, parsed);

    // refresh the specific row to apply highlight
    try {
      if (this.gridApi) {
        this.gridApi.refreshCells({ rowNodes: [params.node], force: true });
      }
    } catch {}

    // Ensure Save/Cancel area updates
    this.cd.detectChanges();
    return true;
  }

  saveEdits() {
    // 1) If there's an active cell editor, stop it so the valueSetter runs and updates editedMap.
    try {
      (this.gridApi as any)?.stopEditing?.();
    } catch (err) {
      console.warn('stopEditing failed or not available on gridApi', err);
    }

    // small async tick to ensure valueSetter ran and editedMap was updated
    setTimeout(() => {
      const idsToSend = new Set<number | string>();
      this.editedMap.forEach((_v, k) => idsToSend.add(k));
      this.touchedSet.forEach((k) => idsToSend.add(k));

      if (idsToSend.size === 0) {
        alert('No changes to save.');
        this.editingMode = false;
        this.cd.detectChanges();
        return;
      }

      const payload: Array<{ id: number; college_order: number }> = [];
      const missingIds: Array<number | string> = [];

      for (const id of idsToSend) {
        const row = this.rowData.find(
          (r: any) => String(r.college_id) === String(id)
        );
        if (!row) {
          missingIds.push(id);
          continue;
        }
        const raw = row.college_order;
        const parsed = String(raw).trim() === '' ? 0 : Number(raw);
        if (
          !Number.isFinite(parsed) ||
          !Number.isInteger(parsed) ||
          parsed < 0
        ) {
          this.error = `Invalid order for id ${id}. Please enter a non-negative integer.`;
          this.cd.detectChanges();
          return;
        }
        payload.push({ id: Number(id), college_order: parsed });
      }

      if (missingIds.length) {
        this.error = `Some rows not found for ids: ${missingIds.join(', ')}`;
        this.cd.detectChanges();
        return;
      }

      console.debug('Updating orders payload:', JSON.stringify(payload));

      this.saving = true;
      this.error = null;
      this.success = null;

      const postSub = this.http
        .post<any>('https://smscap.in/api/updateorder', payload, {
          headers: this.headers(),
        })
        .subscribe({
          next: () => {
            payload.forEach((p) => {
              this.editedMap.delete(p.id);
              this.touchedSet.delete(p.id);
            });
            this.editingMode = false;

            this.success = 'Updated successfully';
            this.cd.detectChanges();

            if (!this.lastSearchParams) {
              if (this.form.valid) this.onSubmit();
              this.saving = false;
              setTimeout(() => {
                this.success = null;
                this.cd.detectChanges();
              }, 2000);
              return;
            }

            const { state_id, district_id, course_id } = this.lastSearchParams;
            const url = `https://smscap.in/api/collegelists?state_id=${state_id}&district_id=${district_id}&course_id=${course_id}`;

            const getSub = this.http
              .get<any>(url, { headers: this.headers() })
              .subscribe({
                next: (data) => {
                  try {
                    this.rowData = this.normalizeArrayResponse(data);
                  } catch {
                    if (data && Array.isArray(data.data)) {
                      this.rowData = data.data;
                    } else if (Array.isArray(data)) {
                      this.rowData = data;
                    } else {
                      const firstArray = Object.values(data || {}).find((v) =>
                        Array.isArray(v)
                      ) as any[] | undefined;
                      this.rowData = firstArray ?? [];
                    }
                  }

                  this.originalOrderMap.clear();
                  this.rowData.forEach((r: any) => {
                    this.originalOrderMap.set(
                      r.college_id,
                      Number(r.college_order ?? 0)
                    );
                  });

                  try {
                    if (
                      this.gridApi &&
                      typeof (this.gridApi as any).applyTransaction ===
                        'function'
                    ) {
                      this.gridApi.applyTransaction({ update: this.rowData });
                      this.gridApi.refreshCells({ force: true });
                    } else if (this.gridApi) {
                      (this.gridApi as any).setRowData(this.rowData);
                      this.gridApi.refreshCells({ force: true });
                    }
                  } catch (err) {
                    console.warn('Grid refresh fallback triggered', err);
                  }

                  setTimeout(() => {
                    this.success = null;
                    this.cd.detectChanges();
                  }, 2000);
                },
                error: () => {
                  this.error = 'Failed to refresh data after save.';
                  this.cd.detectChanges();
                  setTimeout(() => {
                    this.error = null;
                    this.cd.detectChanges();
                  }, 3000);
                },
                complete: () => {
                  this.saving = false;
                  this.cd.detectChanges();
                },
              });

            this.subs.push(getSub);
          },
          error: (err) => {
            this.error = 'Failed to save order. Please try again.';
            this.saving = false;
            this.cd.detectChanges();
          },
        });

      this.subs.push(postSub);
    }, 50);
  }

  cancelEdits() {
    if (this.editedMap.size === 0 && !this.editingMode) return;

    const changedIds = Array.from(this.editedMap.keys());

    this.rowData.forEach((r) => {
      if (changedIds.includes(r.college_id)) {
        const orig = this.originalOrderMap.get(r.college_id);
        if (orig !== undefined) r.college_order = orig;
      }
    });

    const updatedRows = this.rowData.filter((r) =>
      changedIds.includes(r.college_id)
    );
    try {
      if (
        this.gridApi &&
        typeof (this.gridApi as any).applyTransaction === 'function'
      ) {
        this.gridApi.applyTransaction({ update: updatedRows });
        this.gridApi.refreshCells({ force: true });
      } else if (this.gridApi) {
        (this.gridApi as any).setRowData(this.rowData);
        this.gridApi.refreshCells({ force: true });
      }
    } catch {}

    this.editedMap.clear();
    this.touchedSet.clear();
    this.editingMode = false;
    this.cd.detectChanges();
  }

  // -------- Utility ----------
  trackById(index: number, item: Option) {
    return item?.id ?? index;
  }

  // -------------------------
  // Grid height calculation
  // -------------------------
  /**
   * Calculate and set grid height to fit remaining viewport.
   * Measures header + form + footer (save area) and sets .order-grid height.
   */
  private setGridHeight() {
    try {
      const headerEl = document.querySelector(
        '.page-header'
      ) as HTMLElement | null;
      const formEl = document.querySelector(
        '.filter-form'
      ) as HTMLElement | null;
      const footerEl = document.querySelector(
        '.grid-card .d-flex'
      ) as HTMLElement | null; // save/cancel area
      const topPadding = 24; // gap/margins
      const headerH = headerEl ? headerEl.getBoundingClientRect().height : 0;
      const formH = formEl ? formEl.getBoundingClientRect().height : 0;
      const footerH = footerEl ? footerEl.getBoundingClientRect().height : 0;
      const other = headerH + formH + footerH + topPadding;

      const viewportH =
        window.innerHeight || document.documentElement.clientHeight;
      const gridH = Math.max(280, Math.floor(viewportH - other));

      // prefer the gridWrapper element if present
      const gridEl =
        this.gridWrapper && this.gridWrapper.nativeElement
          ? (this.gridWrapper.nativeElement.querySelector(
              '.order-grid'
            ) as HTMLElement)
          : (document.querySelector('.order-grid') as HTMLElement | null);

      if (gridEl) {
        gridEl.style.height = `${gridH}px`;
        gridEl.style.minHeight = `${gridH}px`;

        // force ag-grid relayout
        if (this.gridApi) {
          try {
            this.gridApi.sizeColumnsToFit();
            this.gridApi.refreshCells({ force: true });
          } catch {}
        }
      }
    } catch (err) {
      console.warn('setGridHeight error', err);
    }
  }
}
